import { Icon } from "@/assets/icons";
import { useDropdownToggle } from "@/hooks";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdClose } from "react-icons/md";
import { List, RowComponentProps } from "react-window";
import { LoadingSpinner } from "./Loading";

type Option = {
  value: string | number;
  label: string;
  disabled?: boolean;
  description?: string;
};

type SelectMultipleProps = {
  ref?: React.Ref<HTMLDivElement>;
  options: Option[];
  label?: string;
  placeholder?: string;
  error?: string;
  labelBgClass?: string;
  required?: boolean;
  disabled?: boolean;
  onSelectAll?: (selected: boolean) => void;
  allowSearchInput?: boolean;
  onSearchInputChange?: (value: string) => void;
  value?: Option[];
  onChange?: (value: Option[]) => void;
  onLoadMoreItems?: () => void;
  enableSelectAllOptions?: boolean;
  isLoadingMoreItems?: boolean;
  enableOptionDescription?: boolean;
  stopCheckScrollToBottom?: boolean;
  containerClassName?: string;
};

const SelectMultiple: React.FC<SelectMultipleProps> = ({
  ref: externalRef,
  options,
  label,
  placeholder = "検索...",
  error,
  labelBgClass,
  containerClassName,
  required,
  disabled,
  onSelectAll,
  allowSearchInput,
  onSearchInputChange,
  value,
  onChange,
  onLoadMoreItems,
  enableSelectAllOptions,
  isLoadingMoreItems,
  enableOptionDescription,
  stopCheckScrollToBottom,
}) => {
  // Ref to Search Input
  const itemCount = options.length;
  const itemSize = enableOptionDescription ? 52 : 38;
  const height =
    itemSize * options.length > 240 ? 240 : itemSize * options.length;

  // After call loadMore, delay time to prevent call too many times
  const loadMoreDelay = 200;
  const isStopCheckingLoadMore = useRef(false);

  const [internalValues, setValues] = useState<Map<string | number, Option>>(
    new Map()
  );

  // Debounce search value input
  const [searchValue, setSearchValue] = useState("");
  const debounceSearchValue = useDebounce(searchValue.trim(), 500);

  useEffect(() => {
    onSearchInputChange?.(debounceSearchValue);
  }, [debounceSearchValue]);

  const { ref, open, openDropdown, closeDropdown } =
    useDropdownToggle(externalRef);

  // Reset search value when close select
  useEffect(() => {
    if (!open) {
      setSearchValue("");
    }
  }, [open])

  // Sync internal values with external values
  useEffect(() => {
    setValues((prev) => {
      const newValues = new Map(prev);
      value?.forEach((option) => {
        if (option) {
          newValues.set(option.value, option);
        }
      });
      return newValues;
    });
  }, [value, onChange, options]);

  // Check scroll to bottom to loadmore data
  const handleScroll: React.UIEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      // If is loading or already triggered, do nothing
      if (
        isLoadingMoreItems ||
        isStopCheckingLoadMore.current ||
        stopCheckScrollToBottom
      )
        return;

      const target = e.currentTarget;
      const paddingBottom = 64; // Scroll to 64px from bottom => call load more

      if (
        target.scrollTop + target.clientHeight >=
        target.scrollHeight - paddingBottom
      ) {
        // Đã scroll tới bottom
        onLoadMoreItems?.();

        // Prevent multiple calls
        isStopCheckingLoadMore.current = true;
        setTimeout(() => {
          isStopCheckingLoadMore.current = false;
        }, loadMoreDelay); // Reset after 300ms
      }
    },
    [
      isLoadingMoreItems,
      onLoadMoreItems,
      isStopCheckingLoadMore.current,
      stopCheckScrollToBottom,
    ]
  );

  const updateExternalValue = (values: Map<string | number, Option>) => {
    onChange?.(Array.from(values.values()));
  };

  const handleSelect = (option: Option) => {
    if (internalValues.has(option.value)) {
      const newValues = new Map(internalValues);
      newValues.delete(option.value);
      setValues(newValues);
      updateExternalValue(newValues);
    } else {
      const newValues = new Map(internalValues);
      newValues.set(option.value, option);
      setValues(newValues);
      updateExternalValue(newValues);
    }
  };

  const handleRemove = (option: Option) => {
    const newValues = new Map(internalValues);
    newValues.delete(option.value);
    setValues(newValues);
    updateExternalValue(newValues);
  };

  const handleOpenDropdown = () => {
    if (!disabled) openDropdown();
  };

  const handleIconClick = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
    if (!open) {
      handleOpenDropdown();
    } else {
      closeDropdown();
    }
  };

  const optionClass = useMemo(() => {
    return "flex items-center justify-between gap-4 px-4 text-sm leading-5.5 border-b hover:bg-gray-200 transition-colors duration-300 cursor-pointer disabled:hover-none disabled:opacity-75";
  }, []);

  const handleCheckAll = () => {
    const activeOptionLength = options.filter((opt) => !opt.disabled).length;
    if (internalValues.size === activeOptionLength) {
      setValues(new Map());
      onSelectAll?.(false);
      updateExternalValue(new Map());
    } else {
      const allValues = options.reduce((acc, option) => {
        if (option.disabled) return acc;
        acc.set(option.value, option);
        return acc;
      }, new Map<string | number, Option>());
      setValues(allValues);
      onSelectAll?.(true);
      updateExternalValue(allValues);
    }
    closeDropdown();
  };

  // Clear all selected values
  const handleClear = (e: React.MouseEvent<SVGAElement>) => {
    e.stopPropagation();
    setValues(new Map());
    updateExternalValue(new Map());
    onSelectAll?.(false);
    closeDropdown();
  };

  const Row = ({
    index,
    style,
    options,
  }: RowComponentProps<{
    options: Option[];
  }>) => (
    <button
      type="button"
      style={style}
      className={optionClass}
      onClick={(e) => {
        e.stopPropagation();
        handleSelect(options[index]);
      }}
      disabled={options[index].disabled}
    >
      <div className="text-left space-y-1 flex-1 overflow-hidden">
        <div className="truncate text-sm leading-5.5">
          {options[index].label}
        </div>
        {enableOptionDescription && (
          <div className="text-xs text-light_secondary truncate leading-4.5">
            {options[index].description}
          </div>
        )}
      </div>

      {options[index].disabled && (
        <span className="text-gray-400 ml-auto">無効</span>
      )}
      {internalValues.has(options[index].value) ? (
        <Icon name="checkbox" className="shrink-0" />
      ) : (
        <Icon name="checkbox-uncheck" className="shrink-0" />
      )}
    </button>
  );

  const isCheckAll = useMemo(() => {
    const activeOptionLength = options.filter((opt) => !opt.disabled).length;

    return internalValues.size === activeOptionLength;
  }, [internalValues]);
  const noOptionAvailable = options.length === 0;

  const maxVisibleItems = 24;
  const valueLength = internalValues.size;
  const invisibleItemNumber = valueLength - maxVisibleItems;

  return (
    <div ref={ref} className={cn("relative", containerClassName)}>
      <div className="relative">
        {label && (
          <label
            className={`
                  absolute left-4 top-0 -translate-y-1/2 px-1 text-xs z-[2]
                  ${error ? "text-error_main" : "text-grey-600"}
                  ${labelBgClass || ""}
                `}
            style={{
              background: disabled
                ? labelBgClass ??
                  "linear-gradient(to bottom, transparent 50%, #F4F6F8 50%)"
                : labelBgClass ?? "#ffffff",
            }}
          >
            {label}

            {required && <span className="text-error_main ml-1">*</span>}
          </label>
        )}

        {/* Input  */}
        <div
          onClick={handleOpenDropdown}
          className={`px-[0.88rem] flex items-center justify-between gap-2 h-full py-2 font-normal text-base leading-6 w-full placeholder:text-gray-400/80 placeholder:text-sm
            border border-gray-300 rounded-lg hover:border-charleston_green focus:border-charleston_green outline-none transition-all duration-200
            ${
              error &&
              "!border-error_main hover:border-error_main focus:border-error_main"
            }
            ${
              disabled && "bg-gray-100 cursor-not-allowed hover:border-gray-300"
            }
            ${open && "!border-charleston_green"}
            `}
        >
          <div className="flex flex-wrap gap-2 flex-1">
            {Array.from(internalValues.values())
              .slice(0, maxVisibleItems)
              .map((option) => (
                <div
                  key={option.value}
                  className="bg-grey-200 px-2 py-1 inline-flex items-center gap-1 text-charleston_green text-sm rounded cursor-pointer"
                >
                  <div className="max-w-[150px] overflow-hidden">
                    <div
                      className="truncate text-sm leading-5.5 text-light_primary"
                      title={option.label}
                    >
                      {option.label}
                    </div>
                    {enableOptionDescription && (
                      <div
                        className="text-xs text-light_secondary truncate leading-4.5"
                        title={option.description}
                      >
                        {option.description}
                      </div>
                    )}
                  </div>

                  <Icon
                    name="close"
                    className="size-[18px] hover:opacity-75 transition-opacity duration-200"
                    onClick={() => handleRemove(option)}
                  />
                </div>
              ))}

            {/* Display placeholder when select close and no value */}
            {!valueLength ? (
              <div className={cn("text-gray-400", open && "hidden")}>
                {placeholder}
              </div>
            ) : null}

            {allowSearchInput && open && (
              <input
                type="text"
                // placeholder={!valueLength ? placeholder : ""}
                className="border-none outline-none bg-transparent w-[100px] h-[30px] flex-1"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                disabled={disabled}
                autoFocus
              />
            )}

            {invisibleItemNumber > 0 && (
              <div className="w-full text-center text-sm font-semibold">
                +{invisibleItemNumber} もっと
              </div>
            )}
          </div>

          {!disabled && valueLength > 0 && open && (
            <MdClose onClick={handleClear} className="size-4 cursor-pointer" />
          )}

          {!disabled && (
            <Icon
              name="chevron-down"
              className={`transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
              onClick={handleIconClick}
            />
          )}
        </div>

        {/* Error Message */}
        {error && <p className="text-error_main text-xs mt-1">{error}</p>}
      </div>

      {/* List Options */}
      {open && (
        <div className="absolute z-20 shadow-shadow_light_card bg-white mt-1 w-full rounded-lg border border-transparent_grey_16">
          {/* Check all option */}
          {enableSelectAllOptions && !noOptionAvailable && (
            <div
              className={`${optionClass} bg-grey-300 py-2 ${
                height === 240 && "pr-8"
              }`}
              // style={{ height: itemSize }}
              onClick={handleCheckAll}
            >
              <div>すべて選択</div>
              {isCheckAll ? (
                <Icon name="checkbox" />
              ) : (
                <Icon name="checkbox-uncheck" />
              )}
            </div>
          )}

          <List
            className="w-full border-none shadow-none"
            style={{ height }}
            rowComponent={Row}
            rowCount={itemCount}
            rowHeight={itemSize}
            onScroll={handleScroll}
            rowProps={{ options }}
          />

          {noOptionAvailable && (
            <div className="text-sm text-light_secondary text-center h-14 flex items-center justify-center">
              データがありません
            </div>
          )}

          {isLoadingMoreItems && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm">
              <LoadingSpinner />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { SelectMultiple };
